import { type JSX, type ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';

/**
 * A small history router.
 *
 * ## Why this exists at all
 *
 * Until this phase `consumer-web` rendered one page and nginx served
 * `index.html` for every path, so navigating anywhere returned 200 and showed
 * the homepage again with the query string dropped. That looked like a broken
 * control rather than an unbuilt one, and the tests were green because they
 * asserted the navigation happened — they encoded the bug.
 *
 * Now that discovery is real, those destinations have somewhere to go. This is
 * the smallest thing that gets them there: `pushState`, a `popstate` listener,
 * and a match against a handful of patterns. No dependency, because a router
 * library would bring a data-loading model, a nested-layout model and a
 * migration the rest of the shell has not asked for.
 *
 * ## The rule this does NOT relax
 *
 * A destination still declares its own availability, and `DestinationLink`
 * still refuses to navigate to one that is `coming-soon`. This router makes
 * five paths real; it does not make the other eighty reachable.
 */

export type RouteMatch =
  | { readonly name: 'home' }
  | { readonly name: 'search' }
  | { readonly name: 'category'; readonly categoryId: string }
  | { readonly name: 'offer'; readonly listingId: string }
  | { readonly name: 'storefront'; readonly slug: string }
  | { readonly name: 'pennyVoice' }
  | { readonly name: 'login' }
  | { readonly name: 'site'; readonly page: SitePageKey }
  | { readonly name: 'unknown'; readonly path: string };

/**
 * The website pages (plan 2026-11-01), by path. Each renders inside the
 * frames' own header and footer. The Store is the existing storefront route.
 */
export const SITE_PAGES = {
  '/account': 'account',
  '/orders': 'orders',
  '/bookings': 'bookings',
  '/rewards': 'rewards',
  '/prescriptions': 'prescriptions',
  '/nearby': 'places',
  '/checkout': 'checkout',
  '/help': 'help',
  '/couriers': 'couriers',
  '/partner-with-us': 'partner',
} as const;

export type SitePageKey = (typeof SITE_PAGES)[keyof typeof SITE_PAGES];

export interface Location {
  readonly path: string;
  readonly query: URLSearchParams;
  readonly match: RouteMatch;
}

export interface Navigator {
  readonly location: Location;
  /** Pushes a new entry. Use for a link the customer chose to follow. */
  navigate(to: string): void;
  /** Replaces the current entry. Use for filter changes, so Back still leaves. */
  replace(to: string): void;
}

const RouterContext = createContext<Navigator | undefined>(undefined);

/**
 * Matches a path to a route.
 *
 * Deliberately exhaustive rather than a fall-through to the homepage: an
 * unmatched path is `unknown` and renders a real not-found page. Silently
 * showing the homepage for a mistyped URL is what made the old behaviour so
 * confusing, and it also tells a search engine that every wrong URL is a valid
 * page.
 */
export function matchRoute(pathname: string): RouteMatch {
  const path = normalisePath(pathname);

  if (path === '/') return { name: 'home' };
  if (path === '/search') return { name: 'search' };
  // PENNY realtime voice (beta). The only page nginx lets use the microphone.
  if (path === '/penny/voice') return { name: 'pennyVoice' };
  if (path === '/login') return { name: 'login' };
  if (Object.hasOwn(SITE_PAGES, path)) {
    return { name: 'site', page: SITE_PAGES[path as keyof typeof SITE_PAGES] };
  }

  // Clinics A14: "My clinics" under Account — the list, and one clinic. The page itself decides what
  // to show (only a person a clinic has linked as a patient has any).
  if (path === '/account/clinics' || /^\/account\/clinics\/[A-Za-z0-9][A-Za-z0-9._:-]{0,99}$/.test(path)) {
    return { name: 'site', page: 'account' };
  }

  // Stage 8: one booking — an appointment, or a consultation (`?kind=consultation`).
  if (/^\/bookings\/[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(path)) return { name: 'site', page: 'bookings' };

  // Stage 10: one prescription — a request (`?kind=request`) or a private one (`?kind=consultation`).
  if (/^\/prescriptions\/[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(path)) {
    return { name: 'site', page: 'prescriptions' };
  }

  const category = /^\/categories\/([A-Za-z0-9][A-Za-z0-9._-]{0,63})$/.exec(path);
  if (category?.[1] !== undefined) return { name: 'category', categoryId: category[1] };

  const offer = /^\/offers\/([A-Za-z0-9][A-Za-z0-9._-]{0,63})$/.exec(path);
  if (offer?.[1] !== undefined) return { name: 'offer', listingId: offer[1] };

  const storefront = /^\/storefronts\/([a-z0-9][a-z0-9-]{0,119})$/.exec(path);
  if (storefront?.[1] !== undefined) return { name: 'storefront', slug: storefront[1] };

  return { name: 'unknown', path };
}

/** Trailing slashes collapse, so `/search/` and `/search` are one page. */
function normalisePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname;
}

function readLocation(): Location {
  const url = new URL(window.location.href);

  return {
    path: normalisePath(url.pathname),
    query: url.searchParams,
    match: matchRoute(url.pathname),
  };
}

export function RouterProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [location, setLocation] = useState<Location>(() => readLocation());

  useEffect(() => {
    const onPopState = (): void => setLocation(readLocation());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const go = useCallback((to: string, replace: boolean): void => {
    if (replace) window.history.replaceState({}, '', to);
    else window.history.pushState({}, '', to);

    setLocation(readLocation());
    // A new page starts at the top. Without this, following a result from
    // halfway down a search page lands you halfway down the offer page.
    window.scrollTo({ top: 0 });
  }, []);

  const value: Navigator = {
    location,
    navigate: (to) => go(to, false),
    replace: (to) => go(to, true),
  };

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

/**
 * The navigator, or `undefined` outside a provider.
 *
 * `AppLink` uses this so a link still renders — as an ordinary anchor that
 * triggers a full page load — in a tree without the router. A component that
 * threw here would make the router a hard dependency of every link, and the
 * homepage's own suite renders that page with no router at all.
 */
export function useOptionalNavigator(): Navigator | undefined {
  return useContext(RouterContext);
}

export function useNavigator(): Navigator {
  const value = useContext(RouterContext);

  if (value === undefined) {
    // A hard failure rather than a default: a navigator that silently did
    // nothing would reintroduce exactly the dead-control problem this replaces.
    throw new Error('useNavigator must be used inside a RouterProvider.');
  }

  return value;
}

/**
 * An internal link that keeps the page alive.
 *
 * A real `<a href>`, so it is a link to the browser, to a screen reader, to
 * middle-click and to a crawler. The click handler only intercepts the plain
 * left-click case — modified clicks fall through to the browser so
 * open-in-new-tab keeps working, which is how people actually compare two
 * products.
 */
export function AppLink({
  to,
  children,
  className,
  ariaLabel,
}: {
  readonly to: string;
  readonly children: ReactNode;
  readonly className?: string;
  readonly ariaLabel?: string;
}): JSX.Element {
  const navigator = useOptionalNavigator();

  return (
    <a
      href={to}
      className={className}
      {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
      onClick={(event) => {
        if (event.defaultPrevented) return;
        if (event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        // No router in this tree: fall through to the browser, which loads the
        // page properly rather than doing nothing.
        if (navigator === undefined) return;

        event.preventDefault();
        navigator.navigate(to);
      }}
    >
      {children}
    </a>
  );
}

/** Builds a query string, omitting empty values so URLs stay readable. */
export function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '' || value === false) continue;
    search.set(key, String(value));
  }

  const query = search.toString();
  return query.length > 0 ? `?${query}` : '';
}
